import { useState } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DescriptionList } from '@/components/ui/DescriptionList';
import { resetUserSettings, useSettingsPatch, useSettingsStore } from '@/api/bi/settings';
import { useAuth } from '@/auth/AuthProvider';
import { useSavedViews } from '@/state/savedViews';
import { storage } from '@/lib/storage';
import { notify } from '@/lib/toast';

/** Who you are signed in as, and the two ways to reset your own workspace. */
export function AccountPage() {
  const { user, logout } = useAuth();
  const patchSettings = useSettingsPatch();
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [isWiping, setIsWiping] = useState(false);

  async function wipeLocalData() {
    setIsWiping(true);
    try {
      await resetUserSettings();
      useSettingsStore.getState().setFromServer({ data: {}, version: 0, updated_at: null });
    } catch {
      /* server reset is best-effort; the local clear below still proceeds */
    }
    storage.clearAll({ keepAuth: true });
    useSavedViews.getState().reload();
    setIsWiping(false);
    setConfirmWipe(false);
    notify.success('Local data cleared.');
  }

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Account"
        title="Profile"
        description="Your identity and store scope, as issued by the Dropit backend."
      />

      <Card>
        <CardHeader>
          <CardTitle>Signed in as</CardTitle>
          <Badge tone="primary" className="capitalize">
            {user?.role ?? 'unknown'}
          </Badge>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <DescriptionList
            items={[
              { label: 'Name', value: user?.name },
              { label: 'Username', value: user?.username },
              { label: 'Phone', value: user?.phone_number },
              {
                label: 'Stores',
                value: user?.related_stores?.length
                  ? user.related_stores.join(', ')
                  : 'All stores (admin)',
              },
            ]}
          />
          <div className="pt-1">
            <Button variant="outline" size="sm" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="border-danger/30">
        <CardHeader>
          <CardTitle className="text-danger">Danger zone</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Both actions affect only your own workspace — preferences, saved views and recent stores. Your
            sign-in stays active and no shared data is touched.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                storage.resetPrefs();
                patchSettings({ prefs: {} });
                notify.success('Preferences reset to defaults.');
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Reset preferences
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmWipe(true)}
              className="border-danger/30 text-danger hover:bg-danger/10"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden /> Clear all local data
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Was a `window.confirm`, which can't be styled, can't be dismissed with
          Escape consistently, and is suppressible by the browser. */}
      <ConfirmDialog
        open={confirmWipe}
        onOpenChange={setConfirmWipe}
        title="Clear all local data?"
        description="Preferences, saved views, favourite and recent stores are removed on this device and on the server. Your sign-in stays active."
        confirmLabel="Clear everything"
        tone="destructive"
        isPending={isWiping}
        onConfirm={() => void wipeLocalData()}
      />
    </div>
  );
}
